import { useEffect, useRef, useState, useCallback } from "react";
import { EVENT_STATUS, SEVERITY_LEVELS, KEEP_ALIVE_DELAY, RETRY_DELAY, INITIAL_POLLING_RATE, NOTIFICATION_TYPES } from "../constants/constants";
import { getRandomUUID } from "../../Token";

export const useWebsocket = (url, accountId, token, userId, isRedisMode) => {
  const [isReady, setIsReady] = useState(false);
  const [val, setVal] = useState(null);
  const [ccdValue, setCcdValue] = useState(null);
  const [forensicSearchValue, setForensicSearchValue] = useState(null);
  // Queue, not a single value: backend can emit multiple SEARCH_RESULT_IMAGE_UPLOADED
  // messages in a tight burst (one per uploaded image). A single overwritten slot
  // would drop any message not yet drained by the consumer before the next arrives.
  const [imageUploadValue, setImageUploadValue] = useState([]);

  const clientRef = useRef(null);
  const pingIntervalRef = useRef(null); // Keep-Alive Ping Timer (Active after connect)
  const initialPollingRef = useRef(null); // 5s Interval (Active before connect/during recovery)
  const retryRef = useRef(null); // Retry Timer Reference

  const clearPingInterval = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const clearInitialPolling = useCallback(() => {
    if (initialPollingRef.current) {
      clearInterval(initialPollingRef.current);
      initialPollingRef.current = null;
    }
  }, []);

  const clearRetryTimeout = useCallback(() => {
    if (retryRef.current) {
      clearTimeout(retryRef.current);
      retryRef.current = null;
    }
  }, []);

  const closeConnection = useCallback(() => {
    console.log("Closing WebSocket connection.");
    clearRetryTimeout();
    clearPingInterval();
    clearInitialPolling();
    if (clientRef.current) {
      // Use 1000 for normal closure initiated by the client hook
      clientRef.current.close(1000, "Component unmount or dependency change");
      clientRef.current = null;
    }
    setIsReady(false);
  }, [clearPingInterval, clearInitialPolling, clearRetryTimeout]);

  const setupWebSocket = useCallback(() => {
    // Check if a connection attempt is already underway or open.
    if (clientRef.current && clientRef.current.readyState !== WebSocket.CLOSED) {
      console.log("WS already exists. Skipping setup.");
      return;
    }

    const isCoreDependencyMissing = !userId || !accountId || (!token && !isRedisMode);

    if (isCoreDependencyMissing) {
      console.log("WS connection prerequisites not met. Aborting setup.");
      return;
    }

    // Clear dead client reference if present before creating a new one
    if (clientRef.current) {
      clientRef.current = null;
    }

    // Protocols construction: accountId, token, userId, transactionId
    const transactionId = getRandomUUID();
    const protocols = [accountId, token, userId, transactionId];

    clearPingInterval();
    clearRetryTimeout();
    const client = new WebSocket(url, protocols);
    clientRef.current = client;

    client.onopen = () => {
      setIsReady(true);
      console.log("WS opened");

      try {
        clientRef.current?.send(JSON.stringify({ message: "ping" }));
      } catch (error) {
        console.error("Initial PING failed:", error);
        clearPingInterval();
        retryRef.current = setTimeout(setupWebSocket, RETRY_DELAY);
        return;
      }
      // Set up Keep-Alive (Continuous Watchdog)
      pingIntervalRef.current = setInterval(() => {
        sendPing();
      }, KEEP_ALIVE_DELAY);
    };

    client.onclose = (event) => {
      clearPingInterval();
      clearRetryTimeout();
      clientRef.current = null; // IMPORTANT: Clear the reference so the Fallback setInterval knows to reconnect
      setIsReady(false);

      if (event.code !== 1000) {
        let reason = `WS closed unexpectedly. Code: ${event.code}. Scheduling immediate retry.`;
        if (event.reason) reason += ` Reason: ${event.reason}.`;
        reason += getReason(event.code);
        console.log(reason);
        // Schedule retry immediately upon unexpected close
        retryRef.current = setTimeout(setupWebSocket, RETRY_DELAY);
      } else {
        // Normal Closure (e.g., component cleanup or intentional closure)
        console.log("WS closed manually by app component cleanup or normal server action (code 1000).");
      }
    };

    client.onmessage = (event) => {
      if (!event.data) return;
      let data = null;
      try {
        data = JSON.parse(event.data);
        if(!data || typeof data !== 'object') {
          console.error("Invalid data structure in WS message.");
          return;
        }
      } catch (e) {
        console.error("Failed to parse WS message:", e);
        return;
      }
      if (data.message === "pong") return;
      if (data.resource?.kind === NOTIFICATION_TYPES.DEVICE_VERSION) {
        setCcdValue(data);
      } else if (data.type === NOTIFICATION_TYPES.SEARCH_COMPLETED) {
        setForensicSearchValue(data);
      } else if (data.type === NOTIFICATION_TYPES.SEARCH_RESULT_IMAGE_UPLOADED) {
        // Functional update: always appends to the latest pending queue, even if
        // multiple onmessage calls fire before React commits/flushes in between.
        setImageUploadValue((prev) => [...prev, data]);
      } else {
        if (
          (data.priority === SEVERITY_LEVELS.CRITICAL || data.severity === SEVERITY_LEVELS.CRITICAL) &&
          data.status !== EVENT_STATUS.RESOLVED
        ) {
          setVal(data);
        }
      }
    };

    client.onerror = (err) => {
      console.error("WebSocket Connection Error:", err);
    };
  }, [url, accountId, token, userId, isRedisMode, clearPingInterval, clearRetryTimeout]);

  useEffect(() => {
    console.log("WS Dependency Change detected. Attempting setup/reconnect.");
    // Initial attempt (to start the process without waiting for the first 5s tick)
    setupWebSocket();

    // Fallback: Start the Continuous Initial Polling Interval (The self-managing "wait and try" loop)
    initialPollingRef.current = setInterval(() => {
      // If the connection is dead (null or CLOSED)
      if (!clientRef.current || clientRef.current.readyState === WebSocket.CLOSED) {
          setupWebSocket();
      }
      
      // If the connection is OPEN, clear the polling interval forever.
      // This handles readyState 1 (OPEN). States 0 (CONNECTING) and 2 (CLOSING) are correctly ignored.
      else if (clientRef.current.readyState === WebSocket.OPEN) {
          console.log("Polling detected OPEN connection. Clearing interval.");
          clearInitialPolling();
      }
    }, INITIAL_POLLING_RATE);

    return () => {
      console.log("Hook cleanup: closing connection closing connection due to dependencies or unmount.");
      closeConnection();
    };
  }, [accountId, token, userId, isRedisMode, closeConnection, setupWebSocket, clearInitialPolling]);

  const sendPing = useCallback(() => {
    const ws = clientRef.current;

    // If connection is dead (not OPEN), trigger recovery.
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.log("WS connection lost. Attempting reconnect.");
      clearPingInterval();
      retryRef.current = setTimeout(setupWebSocket, RETRY_DELAY);
      return false;
    } else {
      // Connection is OPEN, Attempt to send PING
      try {
        ws.send(JSON.stringify({ message: "ping" }));
        return true;
      } catch (error) {
        // This means the state transitioned between the check and the send attempt.
        console.error("PING failed during send, forcing reconnect:", error.message);

        // Immediately stop the interval and trigger a reconnect
        clearPingInterval();
        retryRef.current = setTimeout(setupWebSocket, RETRY_DELAY);
        return false;
      }
    }
  }, [clearPingInterval, setupWebSocket]);

  const getReason = (code) => {
    let reason = "";
    if (code == 1001) reason = 'An endpoint is "going away", such as a server going down or a browser having navigated away from a page.';
    else if (code == 1002) reason = "An endpoint is terminating the connection due to a protocol error";
    else if (code == 1003)
      reason =
        "An endpoint is terminating the connection because it has received a type of data it cannot accept (e.g., an endpoint that understands only text data MAY send this if it receives a binary message).";
    else if (code == 1004) reason = "Reserved. The specific meaning might be defined in the future.";
    else if (code == 1005) reason = "No status code was actually present.";
    else if (code == 1006) reason = "The connection was closed abnormally, e.g., without sending or receiving a Close control frame";
    else if (code == 1007)
      reason =
        "An endpoint is terminating the connection because it has received data within a message that was not consistent with the type of the message (e.g., non-UTF-8 [https://www.rfc-editor.org/rfc/rfc3629] data within a text message).";
    else if (code == 1008)
      reason =
        'An endpoint is terminating the connection because it has received a message that "violates its policy". This reason is given either if there is no other sutible reason, or if there is a need to hide specific details about the policy.';
    else if (code == 1009) reason = "An endpoint is terminating the connection because it has received a message that is too big for it to process.";
    else if (code == 1010)
      // Note that this status code is not used by the server, because it can fail the WebSocket handshake instead.
      reason =
        "An endpoint (client) is terminating the connection because it has expected the server to negotiate one or more extension, but the server didn't return them in the response message of the WebSocket handshake.";
    else if (code == 1011)
      reason = "A server is terminating the connection because it encountered an unexpected condition that prevented it from fulfilling the request.";
    else if (code == 1015) reason = "The connection was closed due to a failure to perform a TLS handshake (e.g., the server certificate can't be verified).";
    else reason = "Unknown reason";

    return reason;
  };

  return [isReady, val, setVal, ccdValue, setCcdValue, forensicSearchValue, setForensicSearchValue, imageUploadValue, setImageUploadValue];
};
